import {
  Component,
  input,
  output,
  signal,
  viewChild,
  ElementRef,
  inject,
  effect,
  afterNextRender,
  PLATFORM_ID,
} from '@angular/core';
import { GoogleMapsModule } from '@angular/google-maps';
import { Location } from '@app/models/global.model';
import { MobiUiMapService } from '@mobi/rwc-ui-components-ng-jslib';
import MarkerClusterer from '@googlemaps/markerclustererplus';
import { setTimeout$ } from '@app/helpers/timeout';
import { GOOGLE_MAP_CLUSTER_ICONS, GOOGLE_MAP_STYLES } from '@app/constants';
import { isPlatformServer } from '@angular/common';
import { BreakpointService } from '@app/services/breakpoint.service';
import { hasConsent } from '@app/helpers/consent';
import { CookieConsentOverlayComponent } from '@app/components/cookie-consent-overlay/cookie-consent-overlay.component';

@Component({
  selector: 'mobi-ga-location-map',
  standalone: true,
  imports: [GoogleMapsModule, CookieConsentOverlayComponent],
  templateUrl: './location-map.component.html',
  styleUrl: './location-map.component.scss',
})
export class LocationMapComponent {
  readonly locations = input<Location[]>([]);
  readonly filteredLocations = input<Location[]>([]);
  readonly activeLocation = input<Location | null>(null);

  readonly locationSelected = output<Location>();

  gMap = signal<google.maps.Map | undefined>(undefined);
  readonly mapWrapper = viewChild<ElementRef>('mapWrapper');
  breakpoint = inject(BreakpointService);
  showMapPlaceholder = signal(false);

  #consentCheck = effect(() => {
    if (typeof window === 'undefined') return;
    window.addEventListener('OneTrustGroupsUpdated', () => {
      this.showMapPlaceholder.set(!hasConsent());
    });
  });

  #mapService = inject(MobiUiMapService);
  #platformId = inject(PLATFORM_ID);
  #markers = signal<any[]>([]);
  #activeMarker: any = null;
  #initialCoordinates = { lat: 46.7984, lng: 8.2313 };
  #initialZoom = this.breakpoint.smMax() ? (this.breakpoint.xsMax() ? 7.5 : 7.8) : 8.4;
  #mapStyles = GOOGLE_MAP_STYLES;
  #customMarkerIcon: any = null;
  #customClusterIcons = GOOGLE_MAP_CLUSTER_ICONS;

  #renderMap = effect(async () => {
    if (this.showMapPlaceholder()) return;
    if (!hasConsent()) {
      this.showMapPlaceholder.set(true);
      return;
    }

    if (!this.locations() || !this.locations().length) return;
    if (isPlatformServer(this.#platformId)) return;
    const { Map } = await this.#mapService.importLibrary('maps');
    if (!this.mapWrapper()?.nativeElement) return;
    const markers: any[] = [];
    this.locations().forEach((location: Location) => {
      if (location.marker) {
        markers.push(location.marker);
      }
    });
    this.#markers.set(markers);
    // Initialize Google Maps-dependent objects after library is loaded
    this.#customMarkerIcon = {
      url: '/marker-dot.svg',
      scaledSize: new google.maps.Size(15, 15),
      origin: new google.maps.Point(0, 0),
      anchor: new google.maps.Point(7.5, 15),
    };

    this.gMap.set(
      new Map(this.mapWrapper()!.nativeElement, {
        styles: this.#mapStyles,
        zoom: this.#initialZoom,
        maxZoom: 15,
        center: this.#initialCoordinates,
        mapTypeControl: false,
        fullscreenControl: false,
        zoomControl: false,
        streetViewControl: false,
        isFractionalZoomEnabled: true,
        backgroundColor: '#efefef',
      })
    );
  });

  #setActive = effect(() => {
    if (this.showMapPlaceholder()) return;
    if (!hasConsent()) {
      this.showMapPlaceholder.set(true);
      return;
    }

    if (this.activeLocation()) {
      this.setActiveLocation(this.activeLocation()!);
      return;
    }

    this.resetActiveMarker();
  });

  #clusterUpdate = effect(() => {
    if (this.showMapPlaceholder()) return;
    if (!hasConsent()) {
      this.showMapPlaceholder.set(true);
      return;
    }

    if (!this.gMap() || typeof google === 'undefined') return;
    if (!this.#markers().length) return;

    const map = this.gMap();
    const bounds: google.maps.LatLngBounds = new google.maps.LatLngBounds();
    let fitBounds = !!this.filteredLocations()?.length;

    this.filteredLocations().forEach((location: Location) => {
      bounds.extend((location.marker as any).getPosition());
    });

    if (!this.#markers().length) return;
    new MarkerClusterer(map as google.maps.Map, this.#markers(), {
      styles: this.#customClusterIcons,
      maxZoom: 10,
    });

    if (fitBounds) {
      map!.fitBounds(bounds);
      return;
    }
    map!.setZoom(this.#initialZoom);
    map!.setCenter(this.#initialCoordinates);
  });

  resetActiveMarker(): void {
    if (this.#activeMarker && this.#customMarkerIcon) {
      this.#activeMarker.setIcon(this.#customMarkerIcon);
      this.#activeMarker.setAnimation(null);
      this.#activeMarker = null;
    }
  }

  setActiveLocation(location: Location): void {
    if (typeof google === 'undefined' || !location.marker) return;

    const position = location.marker.getPosition();
    if (!position) return;

    const { lat, lng } = {
      lat: position.lat(),
      lng: position.lng(),
    };

    let activate: any = null;

    this.#markers().forEach((marker: google.maps.Marker) => {
      const position: any = marker.getPosition();
      marker.setIcon(this.#customMarkerIcon);
      if (lat === position.lat() && lng === position.lng()) activate = marker;
    });

    if (!activate) return;

    activate.setIcon({
      url: '/location-pin.svg',
      scaledSize: new google.maps.Size(28, 36),
      origin: new google.maps.Point(0, 0),
      anchor: new google.maps.Point(14, 36),
    });
    activate.setAnimation(google.maps.Animation.BOUNCE);

    this.#activeMarker = activate;
    setTimeout$(() => {
      if (this.#activeMarker) this.#activeMarker.setAnimation(null);
    }, 500);
  }

  onLocationSelected(location: Location): void {
    this.locationSelected.emit(location);
  }
}
